/**
 * M7 S34: the claim panel (spec §2.3, §2.6): the fill-in claim template inside the Logs screen. It renders a
 * `ClaimView` and a `ClaimState` and dispatches `ClaimAction`s; every rule is in `models/claim.ts`. The focused
 * control carries `data-focused` (keyboard and pad focus are the model's). UI events only.
 */
import type { ReactNode } from 'react'
import { canSend, type ClaimAction, type ClaimPanelConfig, type ClaimState, type ClaimView } from './models/claim'

export interface ClaimPanelProps {
  view: ClaimView
  state: ClaimState
  config: ClaimPanelConfig
  dispatch: (a: ClaimAction) => void
}

function focused(on: boolean): { 'data-focused'?: '' } {
  return on ? { 'data-focused': '' } : {}
}

export function ClaimPanel(p: ClaimPanelProps): ReactNode {
  const { view, state: s } = p
  const c = p.config.claim
  const sendAt = view.slots.length
  const wordOf = (i: number): string => {
    const w = s.picks[i]
    return view.slots[i]?.choices.find((ch) => ch.word === w)?.text ?? c.empty
  }
  return (
    <section className="lg-section lg-claim" data-testid="claim-panel" data-tier={view.tier} aria-label={view.title}>
      <h3 className="lg-section__title">{view.title}</h3>
      <p className="lg-row__meta" data-testid="claim-frame">
        {view.frame}. {c.to}: {view.to}
      </p>
      {view.fallback === null ? null : (
        <p className="lg-row__meta" data-testid="claim-fallback">
          {view.fallback}
        </p>
      )}
      <p className="lg-claim__sentence" data-testid="claim-sentence">
        {view.sentence.map((piece, i) =>
          piece.kind === 'text' ? (
            <span key={i}>{piece.text}</span>
          ) : (
            <strong key={i} className="lg-claim__blank">
              [{wordOf(piece.index)}]
            </strong>
          ),
        )}
      </p>
      <ul className="lg-list">
        {view.slots.map((slot) => (
          <li key={slot.index} className="lg-row" data-testid="claim-slot" data-slot={slot.index} {...focused(s.focus === slot.index)}>
            <span className="lg-row__title">{wordOf(slot.index)}</span>
            <span className="lg-row__meta">
              {slot.choices.map((ch) => (
                <button
                  key={ch.word}
                  type="button"
                  tabIndex={-1}
                  className="sui-btn lg-btn"
                  data-testid="claim-word"
                  data-word={ch.word}
                  aria-pressed={s.picks[slot.index] === ch.word}
                  onClick={() => p.dispatch({ type: 'pick', slot: slot.index, word: ch.word })}
                >
                  {ch.text}
                </button>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <p className="lg-row__meta">{c.slotHint}</p>
      {view.retry === null ? null : (
        <p className="lg-notice" data-testid="claim-retry" role="status">
          {view.retry}
        </p>
      )}
      {s.feedback === null ? null : (
        <p className="lg-notice" data-testid="claim-feedback" role="status">
          {s.feedback}
        </p>
      )}
      <div className="sui-head">
        <button
          type="button"
          tabIndex={-1}
          className="sui-btn lg-btn"
          data-testid="claim-send"
          disabled={!canSend(s, view)}
          {...focused(s.focus === sendAt)}
          onClick={() => p.dispatch({ type: 'send' })}
        >
          {c.send}
        </button>
        <button type="button" tabIndex={-1} className="sui-btn lg-btn" data-testid="claim-back" onClick={() => p.dispatch({ type: 'nav', input: 'back' })}>
          {c.back}
        </button>
      </div>
    </section>
  )
}
